/**
 * Form placeholder catalogue + helpers shared by the form editor, the toolbox
 * (send-for-signature validation) and previews.
 *
 * The HTML contract below is load-bearing: the public signing page and the
 * Cloud Functions substitute placeholders with regexes that match these exact
 * class names / attribute orders. Do not rename or reorder attributes.
 *
 *   Patient field:    <span class="patient-field-placeholder" data-field="KEY">{label}</span>
 *   Free text:        <span class="free-text-placeholder" data-label="LABEL">[טקסט חופשי: LABEL]</span>
 *   Signature date:   <span class="signature-date-placeholder">[תאריך חתימה]</span>
 *   Client signature: <div class="signature-placeholder" data-signature-id="N">…</div>
 *   Therapist sig.:   <div class="therapist-signature-block"><img class="therapist-signature-img" …/></div>
 *   Page break:       <div class="page-break"></div>
 */

export const MAX_SIGNATURE_SLOTS = 3;

export const PATIENT_FIELD_PLACEHOLDERS = [
  { field: "firstName", label: "שם פרטי", placeholder: "{שם פרטי}", group: "פרטי מטופל" },
  { field: "lastName", label: "שם משפחה", placeholder: "{שם משפחה}", group: "פרטי מטופל" },
  { field: "fullName", label: "שם מלא", placeholder: "{שם מלא}", group: "פרטי מטופל" },
  { field: "phone", label: "טלפון", placeholder: "{טלפון}", group: "פרטי מטופל" },
  { field: "idNumber", label: "תעודת זהות", placeholder: "{תעודת זהות}", group: "פרטי מטופל" },
  { field: "email", label: "אימייל", placeholder: "{אימייל}", group: "פרטי מטופל" },
  { field: "birthDate", label: "תאריך לידה", placeholder: "{תאריך לידה}", group: "פרטי מטופל" },
  { field: "address", label: "כתובת", placeholder: "{כתובת}", group: "פרטי מטופל" },
  { field: "city", label: "עיר", placeholder: "{עיר}", group: "פרטי מטופל" },
  { field: "clinicName", label: "שם הקליניקה", placeholder: "{שם הקליניקה}", group: "קליניקה" },
  { field: "parent1Name", label: "הורה 1 - שם", placeholder: "{הורה 1 - שם}", group: "הורים" },
  { field: "parent1Phone", label: "הורה 1 - טלפון", placeholder: "{הורה 1 - טלפון}", group: "הורים" },
  { field: "parent1Id", label: "הורה 1 - תעודת זהות", placeholder: "{הורה 1 - תעודת זהות}", group: "הורים" },
  { field: "parent2Name", label: "הורה 2 - שם", placeholder: "{הורה 2 - שם}", group: "הורים" },
  { field: "parent2Phone", label: "הורה 2 - טלפון", placeholder: "{הורה 2 - טלפון}", group: "הורים" },
  { field: "parent2Id", label: "הורה 2 - תעודת זהות", placeholder: "{הורה 2 - תעודת זהות}", group: "הורים" },
  { field: "emergencyContactName", label: "איש קשר חירום - שם", placeholder: "{איש קשר חירום - שם}", group: "איש קשר חירום" },
  { field: "emergencyContactRelation", label: "איש קשר חירום - יחס", placeholder: "{איש קשר חירום - יחס}", group: "איש קשר חירום" },
  { field: "emergencyContactPhone", label: "איש קשר חירום - טלפון", placeholder: "{איש קשר חירום - טלפון}", group: "איש קשר חירום" },
];

export const PATIENT_FIELD_BY_KEY = PATIENT_FIELD_PLACEHOLDERS.reduce((acc, item) => {
  acc[item.field] = item;
  return acc;
}, {});

export const TEXT_COLORS = [
  { name: "שחור", value: "#1f2937" },
  { name: "אפור", value: "#6b7280" },
  { name: "כחול", value: "#3b82f6" },
  { name: "ירוק", value: "#22c55e" },
  { name: "צהוב", value: "#eab308" },
  { name: "כתום", value: "#f97316" },
  { name: "אדום", value: "#ef4444" },
  { name: "סגול", value: "#8b5cf6" },
];

export const PHONETIC_SYMBOLS = {
  consonants: [
    { symbol: "p", name: "פ קשה" },
    { symbol: "b", name: "ב" },
    { symbol: "t", name: "ט" },
    { symbol: "d", name: "ד" },
    { symbol: "k", name: "כ קשה" },
    { symbol: "g", name: "ג" },
    { symbol: "ʔ", name: "עצירה גרונית" },
    { symbol: "f", name: "פ רכה" },
    { symbol: "v", name: "ב רכה" },
    { symbol: "s", name: "ס" },
    { symbol: "z", name: "ז" },
    { symbol: "ʃ", name: "ש" },
    { symbol: "ʒ", name: "ז׳" },
    { symbol: "χ", name: "ח/כ" },
    { symbol: "ʁ", name: "ר גרונית" },
    { symbol: "h", name: "ה" },
    { symbol: "m", name: "מ" },
    { symbol: "n", name: "נ" },
    { symbol: "ŋ", name: "נג" },
    { symbol: "l", name: "ל" },
    { symbol: "r", name: "ר לשונית" },
    { symbol: "j", name: "י" },
    { symbol: "w", name: "ו עיצור" },
    { symbol: "ts", name: "צ" },
    { symbol: "tʃ", name: "צ׳" },
    { symbol: "dʒ", name: "ג׳" },
  ],
  vowels: [
    { symbol: "a", name: "פתח/קמץ" },
    { symbol: "e", name: "סגול/צירה" },
    { symbol: "ɛ", name: "סגול" },
    { symbol: "i", name: "חיריק" },
    { symbol: "o", name: "חולם" },
    { symbol: "u", name: "שורוק/קובוץ" },
    { symbol: "ə", name: "שווא" },
    { symbol: "æ", name: "A פתוח" },
  ],
  diacritics: [
    { symbol: "ˈ", name: "הטעמה ראשית" },
    { symbol: "ˌ", name: "הטעמה משנית" },
    { symbol: "ː", name: "הארכה" },
    { symbol: "̃", name: "נזליות" },
    { symbol: "̥", name: "חסר קול" },
    { symbol: "ʰ", name: "שאיפה" },
    { symbol: "ʷ", name: "עיגול שפתיים" },
    { symbol: "ʲ", name: "המעה" },
  ],
  brackets: [
    { symbol: "/", name: "פונמה (התחלה)" },
    { symbol: "/", name: "פונמה (סיום)" },
    { symbol: "[", name: "צליל (התחלה)" },
    { symbol: "]", name: "צליל (סיום)" },
    { symbol: "→", name: "הופך ל" },
    { symbol: "~", name: "חילופי צלילים" },
    { symbol: "Ø", name: "השמטה" },
    { symbol: "#", name: "גבול מילה" },
  ],
};

export const PHONETIC_TABS = [
  { id: "consonants", label: "עיצורים" },
  { id: "vowels", label: "תנועות" },
  { id: "diacritics", label: "סימנים" },
  { id: "brackets", label: "תחביר" },
];

/**
 * Count the client-signature slots present in a form HTML string.
 */
export const countSignatureSlots = (html) => {
  const matches = String(html || "").match(/class="signature-placeholder"/g);
  return matches ? matches.length : 0;
};

/**
 * Extract the patient-field placeholders used in a form HTML string.
 */
export const extractPatientFieldPlaceholders = (htmlContent) => {
  const placeholders = [];
  PATIENT_FIELD_PLACEHOLDERS.forEach(({ field, placeholder }) => {
    if (htmlContent && htmlContent.includes(`data-field="${field}"`)) {
      placeholders.push({ field, placeholder });
    }
  });
  return placeholders;
};

/**
 * Normalize legacy form HTML (produced by the old contenteditable editor)
 * into markup the structured editor parses faithfully:
 *   - <font color> → <span style="color">
 *   - stray <div> wrappers → <p> (ProseMirror would otherwise merge lines)
 *   - contenteditable attributes stripped
 * Special blocks (signature slots, therapist signature, page breaks) are
 * left untouched. Safe to call on already-normalized HTML.
 */
export const normalizeLegacyFormHtml = (html) => {
  if (!html || typeof document === "undefined") return html || "";
  const SPECIAL = ["signature-placeholder", "therapist-signature-block", "page-break"];
  const BLOCK_TAGS = new Set([
    "DIV", "P", "UL", "OL", "LI", "TABLE", "H1", "H2", "H3", "H4", "H5", "H6", "BLOCKQUOTE", "HR", "PRE",
  ]);
  const isSpecial = (el) => SPECIAL.some((cls) => el.classList.contains(cls));

  const template = document.createElement("template");
  template.innerHTML = html;
  const root = template.content;

  // <font color="…"> → <span style="color: …">
  root.querySelectorAll("font").forEach((font) => {
    const span = document.createElement("span");
    const color = font.getAttribute("color");
    if (color) span.style.color = color;
    while (font.firstChild) span.appendChild(font.firstChild);
    font.replaceWith(span);
  });

  // Strip contenteditable attributes (editor artifacts)
  root.querySelectorAll("[contenteditable]").forEach((el) => el.removeAttribute("contenteditable"));

  // Convert generic <div> wrappers. Iterate until stable since unwrapping
  // can expose nested divs.
  let changed = true;
  let guard = 0;
  while (changed && guard < 20) {
    changed = false;
    guard += 1;
    const divs = Array.from(root.querySelectorAll("div")).filter(
      (div) => !isSpecial(div) && !div.closest(".signature-placeholder, .therapist-signature-block")
    );
    for (const div of divs) {
      const hasBlockChild = Array.from(div.children).some(
        (child) => BLOCK_TAGS.has(child.tagName) || isSpecial(child)
      );
      if (hasBlockChild) {
        // Unwrap: move children up
        while (div.firstChild) div.parentNode.insertBefore(div.firstChild, div);
        div.remove();
      } else {
        const p = document.createElement("p");
        if (div.getAttribute("style")) p.setAttribute("style", div.getAttribute("style"));
        while (div.firstChild) p.appendChild(div.firstChild);
        div.replaceWith(p);
      }
      changed = true;
    }
  }

  const wrapper = document.createElement("div");
  wrapper.appendChild(root.cloneNode(true));
  return wrapper.innerHTML;
};
